"use client";

import { useState } from "react";
import Link from "next/link";
import { ArrowRight, Play } from "lucide-react";
import { cn } from "@/lib/utils";

export type PlaygroundSample = {
  id: string;
  name: string;
  code: string[];
  output: string;
  /** The Playground for this language with the sample already in the editor. */
  href: string;
};

/**
 * `SITE-HOME01`: the Start free band's Playground card. Five small programs,
 * one per language, switched with the buttons; "Run it" opens the real
 * Playground with that program in the editor, where it runs for real. The
 * output shown here is what the program prints, not a live run.
 */
export function PlaygroundSampleCard({
  samples,
  title,
  body,
  runLabel,
  runInPlayground,
  languagesLabel,
  sampleNote,
  demo,
}: {
  samples: PlaygroundSample[];
  title: string;
  body: string;
  runLabel: string;
  runInPlayground: string;
  languagesLabel: string;
  sampleNote: string;
  /** The admin's featured snippet (Platform Settings > Homepage), when there is one. */
  demo: { label: string; href: string } | null;
}) {
  const [picked, setPicked] = useState(0);
  const sample = samples[picked] ?? samples[0];
  if (!sample) return null;

  return (
    <div className="flex flex-col overflow-hidden rounded-2xl border border-neutral-200 bg-white transition-[transform,border-color,box-shadow] duration-200 ease-out hover:-translate-y-0.5 hover:border-primary hover:shadow-card motion-reduce:transition-none motion-reduce:hover:translate-y-0">
      <div className="flex flex-col gap-2.5 bg-neutral-950 p-4">
        <div className="flex flex-wrap gap-1.5" role="group" aria-label={languagesLabel}>
          {samples.map((s, i) => (
            <button
              key={s.id}
              type="button"
              aria-pressed={i === picked}
              onClick={() => setPicked(i)}
              className={cn(
                "inline-flex h-[30px] items-center rounded-lg border px-3 text-[13px] font-semibold outline-none transition-colors duration-150 ease-out focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-neutral-950",
                i === picked ? "border-primary bg-primary text-white" : "border-neutral-700 text-neutral-300 hover:text-white",
              )}
            >
              {s.name}
            </button>
          ))}
        </div>
        <pre
          // Six lines tall whichever sample shows, so switching language never moves the card.
          className="h-[152px] overflow-x-auto overflow-y-hidden rounded-[10px] border border-neutral-800 bg-neutral-900 px-3.5 py-2.5 font-mono text-xs leading-relaxed text-neutral-200 outline-none focus-visible:ring-2 focus-visible:ring-primary sm:text-[13px]"
          aria-label={sampleNote}
          tabIndex={0}
        >
          <code>{sample.code.join("\n")}</code>
        </pre>
        <div className="flex items-center gap-2">
          <span className="inline-flex h-[30px] items-center gap-1.5 rounded-lg bg-primary px-3 text-[13px] font-bold text-white" aria-hidden>
            <Play className="h-3 w-3 fill-current" />
            {runLabel}
          </span>
          <span className="truncate font-mono text-[13px] text-emerald-300">{sample.output}</span>
        </div>
      </div>
      <div className="flex flex-1 flex-col gap-2.5 px-6 py-5">
        <p className="text-xl font-bold text-neutral-900">{title}</p>
        <p className="text-sm leading-relaxed text-neutral-600">{body}</p>
        <div className="flex-1" />
        <Link
          href={sample.href}
          className="inline-flex items-center gap-1.5 self-start rounded text-[15px] font-bold text-primary-ink outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          {runInPlayground}
          <ArrowRight className="h-4 w-4" aria-hidden />
        </Link>
        {demo ? (
          <Link href={demo.href} className="self-start text-sm font-semibold text-neutral-600 underline-offset-4 hover:underline">
            {demo.label}
          </Link>
        ) : null}
      </div>
    </div>
  );
}
